<script setup lang="ts">
import { computed } from 'vue';
import { formatDate } from '~/utils/timestamps';
import { useMediaLightbox } from '~/composables/useMediaLightbox';

const { lightboxState, closeLightbox, updateIndex } = useMediaLightbox();

const currentItem = computed(() => lightboxState.value.items[lightboxState.value.currentIndex]);

const hasPrevious = computed(() => lightboxState.value.currentIndex > 0);
const hasNext = computed(
  () => lightboxState.value.currentIndex < lightboxState.value.items.length - 1
);

const createdAtLabel = computed(() => {
  if (!currentItem.value?.createdAt) return '';
  return formatDate(currentItem.value.createdAt, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
});

// Detect media type
const mediaType = computed(() => {
  const item = currentItem.value;
  if (!item?.mediaType) {
    console.error('Media item missing mediaType:', item);
    throw new Error('Media item must have mediaType field');
  }
  return item.mediaType;
});

const mediaUrl = computed(() => {
  const item = currentItem.value;
  if (!item?.mediaUrl) {
    console.error('Media item missing mediaUrl:', item);
    throw new Error('Media item must have mediaUrl field');
  }
  return item.mediaUrl;
});

const youtubeId = computed(() => {
  const item = currentItem.value;
  if (!item?.mediaUrl) return null;

  // Extract YouTube ID from mediaUrl
  if (item.mediaUrl.includes('youtube.com')) {
    const url = new URL(item.mediaUrl);
    return url.searchParams.get('v') || url.pathname.split('/').pop();
  }

  return null;
});

const posterImage = computed(() => {
  const item = currentItem.value;
  return item?.image?.src;
});

const goToPrevious = () => {
  if (hasPrevious.value) {
    updateIndex(lightboxState.value.currentIndex - 1);
  }
};

const goToNext = () => {
  if (hasNext.value) {
    updateIndex(lightboxState.value.currentIndex + 1);
  }
};
</script>

<template>
  <UModal
    v-model="lightboxState.open"
    fullscreen
    :ui="{
      content: 'bg-black flex flex-col',
      header: 'hidden',
      body: 'flex-1 flex flex-col p-0',
      close: 'hidden',
    }"
  >
    <!-- Custom Header -->
    <div
      class="flex items-center justify-between px-4 sm:px-6 py-4 bg-black/90 backdrop-blur-sm border-b border-neutral-800"
    >
      <div class="flex-1 min-w-0 pr-4">
        <div class="flex items-center gap-3 mb-2">
          <img
            v-if="posterImage"
            :src="posterImage"
            :alt="currentItem.title || 'Media'"
            class="h-10 w-10 rounded-full border border-neutral-700 bg-white dark:bg-neutral-900 object-contain p-1"
          />
          <div class="flex-1 min-w-0">
            <h2 class="text-lg font-semibold text-white truncate">
              {{ currentItem?.title || 'Video' }}
            </h2>
            <p v-if="createdAtLabel" class="text-sm text-neutral-400">
              {{ createdAtLabel }}
            </p>
          </div>
        </div>
        <p v-if="currentItem?.description" class="text-sm text-neutral-300 line-clamp-2">
          {{ currentItem.description }}
        </p>
      </div>
      <button
        type="button"
        class="flex-shrink-0 p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        @click="closeLightbox"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Media Container -->
    <div class="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8 bg-black overflow-auto">
      <div class="w-full max-w-6xl">
        <!-- HLS Video -->
        <MediaLightboxHls
          v-if="lightboxState.open && mediaType === 'hls' && mediaUrl"
          :key="mediaUrl"
          :src="mediaUrl"
          :poster="posterImage"
          :controls="true"
          :autoplay="true"
          class="w-full max-h-[calc(100vh-12rem)] mx-auto rounded-lg shadow-2xl"
        />

        <!-- YouTube Video -->
        <div
          v-else-if="lightboxState.open && mediaType === 'youtube' && youtubeId"
          class="relative w-full"
          style="aspect-ratio: 16/9"
        >
          <iframe
            :key="youtubeId"
            :src="`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`"
            class="absolute inset-0 w-full h-full rounded-lg shadow-2xl"
            frameborder="0"
            allow="autoplay; encrypted-media"
            allowfullscreen
          ></iframe>
        </div>

        <!-- Streamyard Iframe -->
        <div
          v-else-if="lightboxState.open && mediaType === 'streamyard' && mediaUrl"
          class="relative w-full"
          style="aspect-ratio: 16/9"
        >
          <iframe
            :key="mediaUrl"
            :src="mediaUrl"
            :title="currentItem?.title || 'Podcast'"
            class="absolute inset-0 w-full h-full rounded-lg shadow-2xl"
            frameborder="0"
            recording
            allowfullscreen
          ></iframe>
        </div>

        <!-- Unknown media type -->
        <div v-else class="text-center text-neutral-400">
          <p>Unable to load media</p>
        </div>
      </div>
    </div>

    <!-- Navigation Footer -->
    <div
      v-if="lightboxState.items.length > 1"
      class="flex items-center justify-center gap-6 px-4 sm:px-6 py-4 bg-black/90 backdrop-blur-sm border-t border-neutral-800"
    >
      <button
        type="button"
        :disabled="!hasPrevious"
        class="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/10 disabled:hover:bg-transparent"
        @click="goToPrevious"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        <span class="hidden sm:inline">Previous</span>
      </button>

      <div class="text-sm text-neutral-400">
        {{ lightboxState.currentIndex + 1 }} / {{ lightboxState.items.length }}
      </div>

      <button
        type="button"
        :disabled="!hasNext"
        class="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/10 disabled:hover:bg-transparent"
        @click="goToNext"
      >
        <span class="hidden sm:inline">Next</span>
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </UModal>
</template>
